import { Flex, Text, HTMLChakraProps } from '@chakra-ui/react';
import { BaseTooltip, useAuth } from '@invara-dev/react-base';
import { UserType, LobbyType } from '../../features/workflow/types';
import { useWorkflowSocket } from '../../features/workflow/hub/useWorkflowSocket';
import { wfWigitModes } from '../../features/workflow/CoordinatorWidget';
import { useMemo } from 'react';
import { agentScoreDisplay, folioScoreDisplay, wfDisplayType } from '../../features/workflow/displayTypes';
import { ApplicationRoles } from '../../constants/applicationRoles';

type Props = {
  user: Partial<UserType>;
  buttonArea: HTMLChakraProps<'div'>['w'];
  mode?: wfWigitModes;
  scoreFontSize?: HTMLChakraProps<'div'>['fontSize'];
  abbrFontSize?: HTMLChakraProps<'div'>['fontSize'];
  hideAbbr?: boolean;
};
export const ScoreButtons = (props: Props) => {
  const invoke = useWorkflowSocket((r) => r.invoke);
  const auth = useAuth();
  const currentUser = useWorkflowSocket((r) => r.data?.user);

  const scoreDisplay = useMemo(() => {
    switch (props.user?.Group as LobbyType) {
      case LobbyType.Folio:
        return folioScoreDisplay;
      case LobbyType.Inbox:
      default:
        return agentScoreDisplay;
    }
  }, [props.user?.Status, props.user?.Group]);

  // Check if user has permission to view scores
  const canViewScores = useMemo(() => {
    // Res Manager and System Admin can always view scores
    if (
      auth.isInRole([
        ApplicationRoles.systemAdmin,
        ApplicationRoles.supportAdmin,
        ApplicationRoles.resManager,
        ApplicationRoles.developer
      ])
    ) {
      return true;
    }

    // Default: allow viewing own scores.
    return currentUser?.Id === props.user.Id;
  }, [auth, currentUser?.Id, props.user.Id]);

  // If user doesn't have permission to view scores, return nothing
  if (!canViewScores) {
    return null;
  }

  return (
    <>
      {Object.entries({ ...props.user.Scores }).map(([key, value]) => {
        const agentScore = parseInt(key);
        const displayData: wfDisplayType = scoreDisplay[agentScore] as wfDisplayType;
        if (!displayData) {
          return null;
        }
        return (
          <BaseTooltip positioning={{ placement: 'top' }} content={displayData.name ?? ''} key={`${key}-wfScore`}>
            <Flex
              _hover={
                props.mode != wfWigitModes.Lock
                  ? {
                      boxShadow: 'inset 0px 0px 5px white',
                      outline: 'none'
                    }
                  : undefined
              }
              cursor={props.mode != wfWigitModes.Lock ? 'pointer' : 'not-allowed'}
              as={'button'}
              onClick={() => {
                if (props.mode == wfWigitModes.Lock) {
                  return;
                }
                if (props.mode == wfWigitModes.Increment) {
                  invoke('IncrementScore', agentScore, props.user.Id ?? '');
                  return;
                }
                if (props.mode == wfWigitModes.Decrement) {
                  invoke('DecrementScore', agentScore, props.user.Id ?? '');
                  return;
                }
              }}
              userSelect={'none'}
              w={props.buttonArea}
              h={props.buttonArea}
              bgColor={displayData.bgColor}
              justifyContent={'center'}
              alignContent={'center'}
              flexDir={'column'}
              color={'white'}
              shadow={'md'}
              borderRadius={'md'}
              textAlign={'center'}>
              <Text fontSize={props.scoreFontSize} fontWeight={'bold'}>
                {value}
              </Text>
              {!props.hideAbbr && <Text fontSize={props.abbrFontSize ?? 'xs'}>{displayData.abbr}</Text>}
            </Flex>
          </BaseTooltip>
        );
      })}
    </>
  );
};
